import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { toast } from 'react-toastify';
import { FaWhatsapp, FaArrowRightFromBracket, FaRegFaceSmile } from 'react-icons/fa6';

import { clearDataAction } from '../../../redux/channelsDuck';
import { getConversationsAction } from '../../../redux/conversationsDuck';
import { getJcnError } from '../../../const/labelService';
import { logout as logoutRequest } from '../../../utils/userAuth';
import {
  ModalContent,
  SpaceCard,
  SpaceImg,
  SpaceContent,
  SpaceName,
  SpaceMeta,
  WaIcon,
  SpacePhones,
  DropdownFooter,
  CloseButton,
} from './SidebarModal.style';
import { setActualSpaceAction } from '../../../redux/spacesDuck';
import FadeImage from '../../shared/components/FadeImage/FadeImage';
import toastConfig from '../../shared/constants/toaster-config';
import useCloseoutside from '../../../hooks/useCloseOutside';

// Map of country codes to flag emojis
const countryCodeToFlag = {
  '1': '🇺🇸',     // USA/Canada
  '52': '🇲🇽',    // Mexico
  '521': '🇲🇽',   // Mexico mobile
  '54': '🇦🇷',    // Argentina
  '55': '🇧🇷',    // Brazil
  '56': '🇨🇱',    // Chile
  '57': '🇨🇴',    // Colombia
  '58': '🇻🇪',    // Venezuela
  '51': '🇵🇪',    // Peru
  '34': '🇪🇸',    // Spain
  '44': '🇬🇧',    // UK
  '49': '🇩🇪',    // Germany
  '33': '🇫🇷',    // France
  '39': '🇮🇹',    // Italy
  '81': '🇯🇵',    // Japan
  '86': '🇨🇳',    // China
  '91': '🇮🇳',    // India
};

const getCountryFlag = (phone) => {
  // Clean phone number - remove all non-digit characters
  const cleanPhone = phone.replace(/\D/g, '');

  // Check for 3-digit codes first (like 521 for Mexico mobile)
  if (countryCodeToFlag[cleanPhone.slice(0, 3)]) {
    return { flag: countryCodeToFlag[cleanPhone.slice(0, 3)], numberStart: 3, cleanPhone };
  }
  // Then 2-digit codes
  if (countryCodeToFlag[cleanPhone.slice(0, 2)]) {
    const twoDigitCode = cleanPhone.slice(0, 2);

    // Special case: Numbers starting with 55 that are 10 digits are likely Mexican (CDMX area code)
    // Brazilian numbers need 12-13 digits to be complete (55 + 2-digit area + 8-9 local)
    if (twoDigitCode === '55' && cleanPhone.length === 10) {
      // This is likely a Mexican number from CDMX (area code 55 + 8 digits)
      return { flag: '🇲🇽', numberStart: 0, cleanPhone };
    }

    return { flag: countryCodeToFlag[twoDigitCode], numberStart: 2, cleanPhone };
  }
  // Then 1-digit codes
  if (countryCodeToFlag[cleanPhone.slice(0, 1)]) {
    return { flag: countryCodeToFlag[cleanPhone.slice(0, 1)], numberStart: 1, cleanPhone };
  }
  return { flag: '🇲🇽', numberStart: 0, cleanPhone }; // Default to Mexico flag
};

const SidebarModalComponent = ({ show }) => {
  const { ref, isActive, setIsActive } = useCloseoutside(show);
  const token = useSelector((state) => state.user.data.token);
  const spaces = useSelector((state) => state.spaces.data);
  const selectedSpace = useSelector((state) => state.spaces.selected);
  const history = useHistory();
  const dispatch = useDispatch();

  useEffect(() => {
    setIsActive(show);
  }, [show]);

  const logout = async () => {
    try {
      const session = 'front-app';
      await logoutRequest(token, session);
      history.replace('/login');
      dispatch(clearDataAction());
    } catch (error) {
      toast(getJcnError(error.message), toastConfig);
    }
  };

  const changeSpace = async (space) => {
    try {
      await dispatch(setActualSpaceAction(space));
      await dispatch(getConversationsAction(true, 10));
      setIsActive(false);
    } catch (error) {
      console.error(error);
      toast.error(getJcnError(error.message), toastConfig);
    }
  };

  const getChannels = (space) => {
    const channels = space.channels;
    const filteredChannels = channels.filter((channel) => channel?.isActive
      && channel?.platform?.name === 'WHATSAPP'
      && channel?.phone
    );
    if (filteredChannels.length === 0) return null;
    const phones = filteredChannels.map((channel) => {
      const { flag, numberStart, cleanPhone } = getCountryFlag(channel.phone);
      const localNumber = cleanPhone.slice(numberStart);
      return `${flag} ${localNumber}`;
    });
    return phones.join(', ');
  }

  return (
    <ModalContent ref={ref} open={isActive}>
      {spaces?.map((space) => (
        <SpaceCard
          key={space.id}
          selected={selectedSpace?.id === space?.id}
          onClick={() => changeSpace(space)}
        >
          <SpaceImg>
            {space.imageUrl ? (
              <FadeImage image={space.imageUrl} />
            ) : (
              <FaRegFaceSmile style={{ fontSize: '1.2rem', color: '#111827' }} />
            )}
          </SpaceImg>
          <SpaceContent>
            <SpaceName>{space.name}</SpaceName>
            {getChannels(space) && (
              <SpaceMeta>
                <WaIcon>
                  <FaWhatsapp />
                </WaIcon>
                <SpacePhones>{getChannels(space)}</SpacePhones>
              </SpaceMeta>
            )}
          </SpaceContent>
        </SpaceCard>
      ))}
      <DropdownFooter>
        <CloseButton onClick={logout}>
          <FaArrowRightFromBracket />
          Cerrar sesión
        </CloseButton>
      </DropdownFooter>
    </ModalContent>
  );
};

SidebarModalComponent.propTypes = {
  show: PropTypes.bool.isRequired,
};

export default SidebarModalComponent;
